import { useCallback, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { useServiceCore as useService } from '@atlassian/expansion-services/core';
import {
	type FetchInput,
	requestAccess as requestAccessService,
} from '@atlassian/expansion-services/services';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { EntryPointId } from '../LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';

type AnalyticsContext = {
	entryPointId: EntryPointId;
	note?: string;
	source: string;
	sourceAdType: string;
};

const fetchJson = async (input: FetchInput) => {
	const { url, ...options } = input;
	const response = await cfetch(url, options);
	return response.json();
};

export const useRequestAccessService: any = ({
	entryPointId,
	note,
	source,
	sourceAdType,
}: AnalyticsContext) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId } = useSessionData();
	const deps = useMemo(
		() => ({
			createAnalyticsEvent: ({ type, data }: AnalyticsEventPayload) =>
				createAnalyticsEvent({ type, data: { source, ...data } }),
			fetchJSON: fetchJson,
			getCurrentCloudId: () => cloudId,
			getCurrentUserId: () => userId ?? '',
			getCurrentProduct: () => 'confluence',
		}),
		[createAnalyticsEvent, cloudId, source, userId],
	);
	const requestAccess = useService(requestAccessService, deps);

	return useCallback(async (): Promise<boolean> => {
		try {
			const { result = '' } = await requestAccess({
				product: 'loom',
				integration: entryPointId,
				note,
			});
			const success = result === 'PENDING_REQUEST_CREATED';
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'succeeded',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccess',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						result,
						success,
					},
				},
			}).fire();

			return success;
		} catch (error: unknown) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccess',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						error: error instanceof Error ? error.message : 'unknown',
						result: undefined,
						success: false,
					},
				},
			}).fire();

			throw error;
		}
	}, [entryPointId, note, requestAccess, source, sourceAdType, createAnalyticsEvent]);
};

export const useFetchRequestAccessCapability = ({
	entryPointId,
	source,
	sourceAdType,
}: AnalyticsContext): (() => Promise<any>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	return useCallback(async () => {
		const resource = `ari:cloud:loom::site/${cloudId}`;
		try {
			const params = new URLSearchParams({
				resource,
			});
			const url = `/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`;
			const response = await fetchJson({ url });
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'succeeded',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccessCapability',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						resource,
					},
				},
			}).fire();
			return response.results[resource] || undefined;
		} catch (error: unknown) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccessCapability',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						error: error instanceof Error ? error.message : 'unknown',
						resource,
					},
				},
			}).fire();
		}

		return undefined;
	}, [cloudId, createAnalyticsEvent, entryPointId, source, sourceAdType]);
};
